import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { Drug } from '../_models/drug.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth';
import { environment } from 'src/environments/environment';
import { isEqual, uniqWith } from 'lodash-es';

@Injectable()
export class DrugService {
  private authToken;
  private company_id;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.company_id = res.companyID));
  }

  private static getDrugsListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}&is_active=${filter.isActive}`;
    } else {
      if (filter.drug) fromString += `&drug_has=${filter.drug}`;
      if (filter.ndc) fromString += `&ndc_has=${filter.ndc}`;
      if (filter.th_class) fromString += `&th_class_has=${filter.th_class}`;
      if (filter.jcode) fromString += `&jcode_has=${filter.jcode}`;
      if (filter.isActive) fromString += `&is_active=${filter.isActive}`;
    }

    return fromString;
  }

  createDrug(_drug: Drug) {
    const url = `${environment.CORE_BASE}/companies/${this.company_id}/drugs`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<Drug>(url, _drug, { headers });
  }

  findDrugs(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: DrugService.getDrugsListQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${environment.CORE_BASE}/companies/${this.company_id}/drugs`,
      { params },
    );
  }

  updateDrug(_drug: Drug): Observable<any> {
    const url = `${environment.CORE_BASE}/companies/${this.company_id}/drugs/${_drug.id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.put<Drug>(url, _drug, { headers });
  }

  deleteDrug(drugId: number): Observable<any> {
    const url = `${environment.CORE_BASE}/companies/${this.company_id}/drugs/${drugId}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>(url, { headers });
  }

  searchDrugs(term: string): Observable<any> {
    const url = `${environment.CORE_BASE}/companies/${this.company_id}/drugs`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams({
      fromString: 'sort=drug,asc&is_active=true&search_term=' + term,
    });
    return this.http.get<QueryResultsModel>(url, { headers, params });
  }

  searchDrugCFIs(term: string): Observable<any> {
    if (term.length < 3) {
      return of();
    }
    const url = environment.CORE_BASE + `/drugs/autocomplete`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams({ fromString: 'search_term=' + term });
    return this.http.get<QueryResultsModel>(url, { headers, params });
  }

  getDrugsByIds(contactIds: any[]) {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    let ids = '';
    // tslint:disable-next-line:no-shadowed-variable
    contactIds = uniqWith(contactIds, isEqual);
    contactIds.forEach((element) => (ids = ids + '&id=' + element));
    const params = new HttpParams({ fromString: ids });
    return this.http.get<any>(environment.CORE_BASE + '/companies/' + this.company_id + '/drugs', {
      headers,
      params,
    });
  }
  updateDrugImage(file: File, pid): Observable<any> {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = `${environment.CORE_BASE}/companies/${this.company_id}/drugs/${pid}/attachment`;
    return this.http.post(url, { avatar: file.name }, { headers });
  }
  uploadDrugImage(s3, contactFile: File): Observable<any> {
    const url = s3.data.url;
    return this.http.put(url, contactFile, {});
  }
}
